// Guardrails de agenda online: a partir de qué hora puede ofrecerse un slot.
//
// Lógica PURA y sin dependencias -- no toca red, ni Supabase, ni el reloj del
// navegador como fuente de verdad. La hora del negocio se resuelve siempre con
// el timezone del tenant vía Intl, así que es DST-safe.

/**
 * Fila de `organization_booking_settings`, en la forma que consume el
 * frontend. Ownership: ORGANIZATION (TenantId = organizations.id).
 */
import { zonedDateTimeToUtc } from '@/lib/timezone';

export { zonedDateTimeToUtc } from '@/lib/timezone';

export interface BookingGuardrailSettings {
  minimumNoticeMinutes: number;
  maxDaysAhead: number;
  protectionEnabled: boolean;
  protectionStartMinutes: number;
  protectionEndMinutes: number;
  protectionBufferMinutes: number;
}

export interface BookingGuardrailRefreshSource {
  loadSettings: () => Promise<BookingGuardrailSettings>;
  loadServerNow: () => Promise<Date>;
}

export interface RefreshedBookingGuardrailContext {
  settings: BookingGuardrailSettings;
  serverNow: Date;
}

/** Ventana horaria en minutos desde la medianoche local. */
export interface ProtectionWindow {
  /** Inicio, INCLUSIVE. */
  startMinutes: number;
  /** Fin, EXCLUSIVE. */
  endMinutes: number;
}

export interface EarliestBookableInput {
  /** Instante actual real (absoluto). */
  now: Date;
  /** Día que el cliente quiere reservar, 'YYYY-MM-DD' en hora local del negocio. */
  targetDate: string;
  /** Apertura del negocio ese día, minutos desde medianoche. `null` = cerrado. */
  openingMinutes: number | null;
  /** Anticipación mínima configurable. */
  minimumNoticeMinutes: number;
  protectionEnabled: boolean;
  /** Ventana en que aplica el margen extra. Puede cruzar medianoche (22:00→08:00). */
  protectionWindow: ProtectionWindow | null;
  /** Margen añadido DESPUÉS de la apertura cuando la protección aplica. */
  protectionBufferMinutes: number;
  /** IANA, p. ej. 'America/Santiago'. */
  timezone: string;
}

export interface EarliestBookableResult {
  /** Instante absoluto más temprano reservable. `null` si el día está cerrado. */
  earliest: Date | null;
  /** Minutos desde medianoche local del día objetivo. `null` si cerrado. */
  earliestMinutes: number | null;
  /** ¿Se aplicó el margen de protección de inicio de jornada? */
  protectionApplied: boolean;
  /** Por qué quedó en ese valor -- alimenta el preview y el debugging. */
  reason: 'closed' | 'notice' | 'opening' | 'opening_protected';
}

const MINUTES_PER_DAY = 24 * 60;

/** Fecha local del negocio ('YYYY-MM-DD') para un instante absoluto. */
export function localDateString(date: Date, timeZone: string): string {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return dtf.format(date); // en-CA formatea como YYYY-MM-DD
}

/** Minutos desde la medianoche local del negocio para un instante absoluto. */
export function localTimeMinutes(date: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  });
  const parts = dtf.formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  return get('hour') * 60 + get('minute');
}

/**
 * ¿Está `minutes` dentro de la ventana? Inicio INCLUSIVE, fin EXCLUSIVE.
 * Soporta ventanas que cruzan medianoche (22:00 → 08:00).
 */
export function isWithinWindow(minutes: number, window: ProtectionWindow): boolean {
  const { startMinutes, endMinutes } = window;
  if (startMinutes === endMinutes) return false; // ventana degenerada: no protege
  if (startMinutes < endMinutes) {
    return minutes >= startMinutes && minutes < endMinutes;
  }
  // Cruza medianoche: [start, 24:00) ∪ [00:00, end)
  return minutes >= startMinutes || minutes < endMinutes;
}

/** Suma días calendario sin depender del timezone del runner. */
function addDaysToDateString(dateStr: string, days: number): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return [
    shifted.getUTCFullYear(),
    String(shifted.getUTCMonth() + 1).padStart(2, '0'),
    String(shifted.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

/**
 * Decide qué jornada protege la franja actual.
 *
 * Una ventana normal protege la apertura del día actual. En una ventana que
 * cruza medianoche, el segmento tarde protege la jornada siguiente y el
 * segmento madrugada protege la jornada actual.
 */
export function protectionAppliesToTargetDate(
  now: Date,
  targetDate: string,
  window: ProtectionWindow,
  timezone: string,
): boolean {
  const { startMinutes, endMinutes } = window;
  if (startMinutes === endMinutes) return false;

  const localDate = localDateString(now, timezone);
  const localMinutes = localTimeMinutes(now, timezone);

  if (startMinutes < endMinutes) {
    return localMinutes >= startMinutes
      && localMinutes < endMinutes
      && targetDate === localDate;
  }

  if (localMinutes >= startMinutes) {
    return targetDate === addDaysToDateString(localDate, 1);
  }

  return localMinutes < endMinutes && targetDate === localDate;
}

/**
 * Refresca settings y reloj como una sola decisión testeable. El hook aporta
 * las lecturas remotas; Promise.all evita regenerar slots con solo la mitad
 * del contexto actualizada.
 */
export async function refreshBookingGuardrailContext(
  source: BookingGuardrailRefreshSource,
): Promise<RefreshedBookingGuardrailContext> {
  const [settings, serverNow] = await Promise.all([
    source.loadSettings(),
    source.loadServerNow(),
  ]);

  if (Number.isNaN(serverNow.getTime())) {
    throw new Error('Invalid booking server clock');
  }

  return { settings, serverNow };
}

/**
 * Hora más temprana reservable para `targetDate`.
 *
 * La ventana de protección se evalúa sobre la hora en que el CLIENTE ESTÁ
 * RESERVANDO, no sobre la hora de la cita. Una ventana normal protege la
 * apertura del día actual. En una ventana cross-midnight, el segmento tarde
 * protege la apertura de mañana y el segmento madrugada la de hoy.
 */
export function calculateEarliestBookableTime(
  input: EarliestBookableInput,
): EarliestBookableResult {
  const {
    now,
    targetDate,
    openingMinutes,
    minimumNoticeMinutes,
    protectionEnabled,
    protectionWindow,
    protectionBufferMinutes,
    timezone,
  } = input;

  if (openingMinutes === null) {
    return { earliest: null, earliestMinutes: null, protectionApplied: false, reason: 'closed' };
  }

  const notice = Math.max(0, minimumNoticeMinutes || 0);
  const buffer = Math.max(0, protectionBufferMinutes || 0);

  const baseEarliest = new Date(now.getTime() + notice * 60_000);

  const insideProtectionWindow = protectionEnabled
    && protectionWindow !== null
    && protectionAppliesToTargetDate(now, targetDate, protectionWindow, timezone);

  const openingFloorMinutes = insideProtectionWindow
    ? openingMinutes + buffer
    : openingMinutes;

  const openingFloor = zonedDateTimeToUtc(targetDate, openingFloorMinutes, timezone);

  const earliest = baseEarliest.getTime() >= openingFloor.getTime() ? baseEarliest : openingFloor;
  const noticeWon = earliest.getTime() === baseEarliest.getTime()
    && baseEarliest.getTime() !== openingFloor.getTime();

  // Minutos locales del día objetivo. Puede superar 1440 si la anticipación
  // empuja más allá de medianoche: quien consume esto debe tratarlo como
  // "no hay slot ese día".
  const dayStart = zonedDateTimeToUtc(targetDate, 0, timezone);
  const earliestMinutes = Math.round((earliest.getTime() - dayStart.getTime()) / 60_000);

  return {
    earliest,
    earliestMinutes,
    protectionApplied: insideProtectionWindow,
    reason: noticeWon ? 'notice' : (insideProtectionWindow ? 'opening_protected' : 'opening'),
  };
}

export interface ProtectionPreviewInput {
  /** Apertura del día que se está previsualizando. `null` = cerrado. */
  openingMinutes: number | null;
  protectionEnabled: boolean;
  protectionStartMinutes: number;
  protectionEndMinutes: number;
  protectionBufferMinutes: number;
  minimumNoticeMinutes: number;
}

/**
 * Frase que explica al tenant qué hará la regla, con SUS valores.
 *
 * Devuelve `null` cuando no hay nada honesto que mostrar (día cerrado o
 * protección apagada): mejor no enseñar nada que inventar un ejemplo falso.
 */
export function buildProtectionPreview(input: ProtectionPreviewInput): string | null {
  const {
    openingMinutes,
    protectionEnabled,
    protectionStartMinutes,
    protectionEndMinutes,
    protectionBufferMinutes,
    minimumNoticeMinutes,
  } = input;

  if (openingMinutes === null) return null;
  if (!protectionEnabled) return null;
  if (protectionStartMinutes === protectionEndMinutes) return null;

  const opening = minutesToTimeString(openingMinutes);
  const windowStart = minutesToTimeString(protectionStartMinutes);
  const windowEnd = minutesToTimeString(protectionEndMinutes);
  const firstSlot = minutesToTimeString(openingMinutes + Math.max(0, protectionBufferMinutes || 0));

  const crossesMidnight = protectionStartMinutes > protectionEndMinutes;
  const noticeNote = minimumNoticeMinutes > 0
    ? ` La anticipación mínima de ${formatDuration(minimumNoticeMinutes)} se sigue aplicando, así que la primera cita puede ser aún más tarde.`
    : '';

  if (crossesMidnight) {
    return `Durante la franja nocturna de las ${windowStart} a las ${windowEnd}, la protección se aplicará al inicio de la jornada inmediatamente siguiente. Si abre a las ${opening}, la primera cita disponible será a partir de las ${firstSlot}.${noticeNote}`;
  }

  return `Hoy abres a las ${opening}. Si un cliente reserva entre las ${windowStart} y las ${windowEnd} para hoy, la primera cita disponible será a partir de las ${firstSlot}.${noticeNote}`;
}

/** 90 -> '1 h 30 min'. Para textos de interfaz. */
export function formatDuration(minutes: number): string {
  const safe = Math.max(0, Math.round(minutes || 0));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} h`;
  return `${hours} h ${mins} min`;
}

/** 'HH:MM' -> minutos. Devuelve null si no es una hora válida. */
export function timeStringToMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const mins = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return null;
  if (hours < 0 || hours > 23 || mins < 0 || mins > 59) return null;
  return hours * 60 + mins;
}

/** minutos -> 'HH:MM'. Envuelve más allá de 24h para ventanas cross-midnight. */
export function minutesToTimeString(minutes: number): string {
  const normalized = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(normalized / 60);
  const mins = normalized % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}
